<template>
    <b-container>
      <header class="header">
        <TheTitle class="header__title">管理附屬帳戶</TheTitle>
        <label class="addButton header__search">
        <ButtonSubMerchant class="isMobile" :type="'add'"></ButtonSubMerchant>
        </label>
      </header>
      <h4>附屬帳戶列表</h4>
      <div style="font-size: 0.85em;">
        <b>附屬帳戶登入連結（供掃描二維碼用）：</b>
        <a href="https://www.ulifestyle.com.hk/merchantcenter/qr-scanner/subMerchantLogin" target="_blank">
          https://www.ulifestyle.com.hk/merchantcenter/qr-scanner/subMerchantLogin
        </a>
      </div>
      <section class="table-content">
        <BaseFilterToolbar @reset="initialize" :loading="loading">
          <div class="d-flex">
              <label>
              <b-form-select class="filter keyword-select" v-model="filter.keywordType">
                <b-form-select-option
                    v-for="option in filteredKeywordSelectOptions"
                    :value="option.value"
                    :key="option.text">
                    {{ option.text }}
                  </b-form-select-option>
              </b-form-select>
            </label>
            <label id="keyword-filter">
              <input
                v-model.trim="keyword"
                type="text"
                class="form-control"
                placeholder="關鍵字..."
                aria-label="關鍵字..."
                aria-describedby="button-addon1"
              />
            </label>
          </div>
        </BaseFilterToolbar>
        <DataTable
          v-show="list"
          :fields="fields"
          :mobileFields="mobileFields"
          :items="list"
          :total-result="total"
          :options.sync="options"
          :loading="loading"
          @row-selected="onRowSelected"
          mobile-searchable
          showRowDetails
        >
          <template v-slot:cell(status)="{ value }">
            <StatusLabel :item="value"></StatusLabel>
          </template>
          <template v-slot:cell(password)>
            ・・・・・・
          </template>
          <template v-slot:cell(id)="{ item }">
           <div @click="handleEdit(item, item.id)">
            <ButtonSubMerchant :type="'edit'"
                               :id="subMerchantId">
            </ButtonSubMerchant>
           </div>
          </template>

          <!-- mobile only -->
          <template v-slot:password>
            ・・・・・・
          </template>
          <template v-slot:status="{ value }">
            <StatusLabel :item="value"></StatusLabel>
          </template>
          <template v-slot:id="{ item }">
           <div class="mobile-edit-wrapper" @click="handleEdit(item, item.id)">
            <ButtonSubMerchant :type="'edit'"
                                class="mobile-edit"
                               :id="subMerchantId">
            </ButtonSubMerchant>
           </div>
          </template>
          <!-- end of mobile only-->
        </DataTable>
        <b-skeleton-table v-if="!list" :rows="15" :columns="5"></b-skeleton-table>
      </section>
      <ModalEdit :id="subMerchantId" :item="item"></ModalEdit>
      <ModalAdd @update="updateList"></ModalAdd>
    </b-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { scheduled, asyncScheduler } from 'rxjs';
import {
  switchMap, debounceTime, map, pluck, mergeAll,
} from 'rxjs/operators';
import {
  BFormSelect, BFormSelectOption, BContainer, BSkeletonTable,
} from 'bootstrap-vue';
import { getSubMerchantStatus } from '@/helpers/coupon-status';
import dataTableMixin from '../../mixins/data-table';
import DataTable from '../../components/DataTable.vue';
import TheTitle from '../../components/TheTitle.vue';
import StatusLabel from '../../components/StatusLabel.vue';
import ButtonSubMerchant from '../../components/sub-merchant/ButtonSubMerchant.vue';
import ModalEdit from '../../components/sub-merchant/ModalEdit.vue';
import ModalAdd from '../../components/sub-merchant/ModalAdd.vue';
import BaseFilterToolbar from '../../components/BaseFilterToolbar.vue';

export default {
  mixins: [dataTableMixin],
  components: {
    DataTable,
    TheTitle,
    BContainer,
    BSkeletonTable,
    BFormSelect,
    BFormSelectOption,
    StatusLabel,
    ButtonSubMerchant,
    ModalEdit,
    ModalAdd,
    BaseFilterToolbar,
  },
  data: () => ({
    fields: [
      { key: 'name', label: '分店／店鋪名稱', sortable: true },
      { key: 'username', label: '附屬帳戶登入名稱', sortable: true },
      { key: 'password', label: '密碼', sortable: false },
      {
        key: 'status',
        label: '狀態',
        sortable: true,
        formatter: (value, key, item) => getSubMerchantStatus(item.status),
      },
      {
        key: 'id',
        label: '',
        sortable: false,
        thStyle: {
          display: 'none',
        },
      },
    ],
    mobileFields: [
      { key: 'name', label: '分店／店鋪名稱', sortable: true },
      { key: 'username', label: '附屬帳戶登入名稱', sortable: true },
    ],
    filter: {
      keywordType: 'all',
      id: undefined,
      name: undefined,
      username: undefined,
    },
    options: {
      page: 1,
      size: 15,
      order: 'desc',
    },
    keyword: '',
    keywordSelectOptions: [
      { value: 'all', text: '所有關鍵字' },
      { value: 'id', text: '商戶編號' },
      { value: 'name', text: '分店／店鋪名稱' },
      { value: 'username', text: '附屬帳戶登入名稱' },
    ],
    selected: [],
    subMerchantId: '',
    item: [],
  }),
  computed: {
    ...mapGetters(['role']),
    ...mapState(['user']),
    id() {
      return (this.role === 'admin' || this.role === 'sale') ? this.$route.params.id : this.user.id;
    },
  },
  subscriptions() {
    const getData$ = (data) => this.httpRequest$({
      method: 'GET',
      url: `/agc-${this.role}-api/submerchants`,
    }, data);

    return {
      list: scheduled([
        ...this.scheduleTasks,
      ], asyncScheduler).pipe(
        mergeAll(),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        pluck('data'),
        switchMap(getData$),
      ),
    };
  },
  methods: {
    onRowSelected(items) {
      this.selected = items;
    },
    handleEdit(item, id) {
      this.item = item;
      this.subMerchantId = id;
    },
    initialize() {
      this.filter.keywordType = 'all';
      this.keyword = '';
      this.filter.id = undefined;
      this.filter.name = undefined;
      this.filter.username = undefined;
      setTimeout(() => {
        this.loading = false;
      }, 550);
      // when user updates filter too fast, the loading state will not set back to false
      // this time must be bigger than debounceTime's time
    },
    updateList(item) {
      this.list.unshift(item);
    },
  },
};
</script>

<style scoped lang="scss">
  @import '../../assets/scss/data-table/list-table-content';
  @import '../../assets/scss/listing/header';

  @include respond(phone) {
    .isMobile{
      display: none;
    }
    &::v-deep {
      .data-table{
        .right-corner{
          right: 20%;
        }
        div {
          overflow: initial !important;
        }
        .mobile-edit-wrapper{
          width: 100%;
          position: relative;
          .mobile-edit{
            right: 5px;
            bottom: 10px;
            position: absolute;
          }
        }
      }
    }
  }
  @include respond(tab-landscape) {
    .header{
      justify-content: end;
    }
  }
  .addButton {
    padding-top:25px;
  }

  h4 {
    color: #174169;
    font-size: 18px;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 25px;
    margin-bottom: 1rem;
  }
</style>
